import React, { useState, useId } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface AnimatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

export const AnimatedInput = React.forwardRef<HTMLInputElement, AnimatedInputProps>(
  ({ label, className, error, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const generatedId = useId();
    const inputId = props.id || generatedId;

    return (
      <div className="relative">
        <Input
          {...props}
          id={inputId}
          ref={ref}
          aria-label={label}
          className={cn(
            'h-14 pt-2 transition-all duration-200',
            'focus:outline-none',
            isFocused || hasValue ? 'border-blue-400' : '',
            error ? 'border-red-500' : '',
            className
          )}
          style={{ boxShadow: 'none' }}
          value={props.value}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            setHasValue(e.target.value !== '');
            props.onBlur?.(e);
          }}
          onChange={(e) => {
            setHasValue(e.target.value !== '');
            props.onChange?.(e);
          }}
        />
        <label
          htmlFor={inputId}
          className={cn(
            'pointer-events-none absolute left-3 px-1 transition-all duration-200',
            isFocused || hasValue
              ? '-top-2.5 bg-background text-xs'
              : 'top-[1.4em] text-sm text-gray-600',
            error ? 'text-red-500' : isFocused || hasValue ? 'text-blue-400' : ''
          )}
        >
          <span className="px-1">{label}</span>
        </label>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);

AnimatedInput.displayName = 'AnimatedInput';
